import React, { useState, useEffect } from 'react';
import qs from 'query-string';
import axios from 'axios';
import {
    withStyles,
    Button,
    Typography,
    CircularProgress
} from '@material-ui/core';
import { loadStripe } from '@stripe/stripe-js';
import { Auth } from 'aws-amplify';
import { green } from '@material-ui/core/colors';
import ContentHeader from '../components/ContentHeader';
import ButtonLink from '../components/ButtonLink';
import SendTicketModal from '../components/SendTicketModal';

const styles = theme => ({
    root: {
        textAlign: 'center',
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative',
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    spinner: {
        marginTop: '60px',
        marginBottom: '60px'
    },
    heading: {
        marginBottom: '10px'
    },
    subheading: {
        marginTop: '20px'
    },
    paragraph: {
        fontSize: '16px',
        marginBottom: '10px',
        fontWeight: '100'
    },
    inner: {
        padding: '20px 10px'
    },
    button: {
        margin: '5px 5px'
    },
    ticketButtons: {
        display: 'flex',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column'
        }
    },
    success: {
        fontSize: '18px',
        color: green[500]
    }
});

const BuyAnother = ({ classes, year, clientSlug, serviceId, ticketId }) => {
    if (year && clientSlug && serviceId && ticketId) {
        return (
            <Typography className={classes.paragraph} variant="body2">
                <ButtonLink
                    className={classes.button}
                    variant="outlined"
                    color="primary"
                    to={`/create-ticket/${year}/${clientSlug}/${serviceId}/${ticketId}`}
                >
                    Buy another
                </ButtonLink>
            </Typography>
        );
    }

    return null;
};

const normalizeStripeAccountId = id => {
    if (typeof id !== 'string') {
        return null;
    }

    const trimmedId = id.trim();
    if (!trimmedId) {
        return null;
    }

    if (trimmedId.startsWith('acct_')) {
        return trimmedId;
    }

    return `acct_${trimmedId}`;
};

const BankOrderConfirmed = ({ classes, location }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [paymentIntent, setPaymentIntent] = useState(null);
    const [customerTicketId, setCustomerTicketId] = useState(null);
    const [ticketName, setTicketName] = useState(null);
    const [showSendModal, setShowSendModal] = useState(false);

    const {
        csa: strippedStripeAccountId,
        payment_intent_client_secret: paymentIntentClientSecret,
        encrypted_ctid: encryptedTicketId,
        year,
        client_slug: clientSlug,
        service_id: serviceId,
        ticket_id: ticketId
    } = qs.parse(location.search);

    const normalizedStripeAccountId = normalizeStripeAccountId(
        strippedStripeAccountId
    );

    useEffect(() => {
        let isMounted = true;

        setIsLoading(true);
        setPaymentIntent(null);
        setCustomerTicketId(null);
        setTicketName(null);

        const fetchTicket = async () => {
            try {
                const currentSession = await Auth.currentSession();

                const response = await axios.get(
                    [
                        process.env.REACT_APP_PAYMENT_API_ENDPOINT,
                        'customer-ticket',
                        'encrypted',
                        encryptedTicketId
                    ].join('/'),
                    {
                        headers: {
                            Authorization: currentSession
                                .getIdToken()
                                .getJwtToken()
                        }
                    }
                );

                return response.data || {};
            } catch (e) {
                console.error(e);
                // we treat this like an unknown ticket state and show
                // the pending buttons
            }

            return {};
        };

        (async () => {
            try {
                const stripe = await loadStripe(
                    process.env.REACT_APP_STRIPE_PUB_KEY,
                    normalizedStripeAccountId
                        ? { stripeAccount: normalizedStripeAccountId }
                        : undefined
                );

                if (!stripe) {
                    throw new Error(
                        'Stripe failed to initialize. Please verify the publishable key and account configuration.'
                    );
                }

                const result = await stripe.retrievePaymentIntent(
                    paymentIntentClientSecret
                );

                if (result.error) {
                    throw new Error(result.error);
                }

                if (result.paymentIntent.status === 'succeeded') {
                    const {
                        id = null,
                        service_name: serviceName = null
                    } = await fetchTicket();

                    if (isMounted) {
                        setCustomerTicketId(id);
                        setTicketName(serviceName);
                    }
                }

                if (isMounted) {
                    setPaymentIntent(result.paymentIntent);
                }
            } catch (error) {
                console.error('Error retrieving payment intent:', error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [strippedStripeAccountId, paymentIntentClientSecret, encryptedTicketId]);

    if (isLoading) {
        return (
            <div className={classes.root}>
                <CircularProgress
                    id="loading-spinner"
                    className={classes.spinner}
                />
            </div>
        );
    }

    if (!paymentIntent) {
        return (
            <div className={classes.root}>
                <ContentHeader
                    text="Error Retrieving Order"
                    id="order-failed-heading"
                />
                <div className={classes.inner}>
                    <Typography className={classes.paragraph} variant="body2">
                        There was a problem retrieving your order details. If
                        you reached this page directly or your payment is still
                        being processed, please check your email for updates or
                        contact support if you have any questions.
                    </Typography>
                </div>
            </div>
        );
    }

    if (paymentIntent.status === 'succeeded') {
        const ticketUrl = [
            process.env.REACT_APP_VIEW_TICKET_BASE_URL,
            customerTicketId
        ].join('');

        return (
            <>
                <div className={classes.root}>
                    <SendTicketModal
                        show={showSendModal}
                        setShow={setShowSendModal}
                        customerTicketId={customerTicketId}
                        ticketName={ticketName}
                    />
                    <ContentHeader
                        text="Order Confirmed"
                        id="order-confirmed-heading"
                    />
                    <div className={classes.inner}>
                        <Typography
                            className={classes.paragraph}
                            variant="body2"
                        >
                            Congratulations! Your order was successful and your
                            ticket{' '}
                            {customerTicketId
                                ? 'is now available to use.'
                                : 'will be available to use shortly.'}
                        </Typography>
                        <Typography
                            className={classes.paragraph}
                            variant="body2"
                        >
                            Tickets will be scanned on board the vehicle and
                            will need to be presented by the passenger.
                        </Typography>
                        <Typography
                            className={classes.paragraph}
                            variant="body2"
                        >
                            Access your ticket using the below options:
                        </Typography>
                        <div className={classes.ticketButtons}>
                            {customerTicketId && (
                                <>
                                    <Typography
                                        className={classes.paragraph}
                                        variant="body2"
                                    >
                                        <Button
                                            className={classes.button}
                                            variant="outlined"
                                            color="primary"
                                            component="a"
                                            href={ticketUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View Ticket
                                        </Button>
                                    </Typography>
                                    <Typography
                                        className={classes.paragraph}
                                        variant="body2"
                                    >
                                        <Button
                                            className={classes.button}
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                setShowSendModal(true);
                                            }}
                                        >
                                            Send Ticket
                                        </Button>
                                    </Typography>
                                    <Typography
                                        className={classes.paragraph}
                                        variant="body2"
                                    >
                                        <Button
                                            className={classes.button}
                                            variant="outlined"
                                            color="primary"
                                            component="a"
                                            href={`${ticketUrl}?print=true`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Print Ticket
                                        </Button>
                                    </Typography>
                                </>
                            )}
                            <BuyAnother
                                {...{
                                    classes,
                                    year,
                                    clientSlug,
                                    serviceId,
                                    ticketId
                                }}
                            />
                            {customerTicketId === null && (
                                <Typography
                                    className={classes.paragraph}
                                    variant="body2"
                                >
                                    <ButtonLink
                                        className={classes.button}
                                        variant="outlined"
                                        color="primary"
                                        to="/"
                                    >
                                        My Tickets
                                    </ButtonLink>
                                </Typography>
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (paymentIntent.status === 'processing') {
        return (
            <div className={classes.root}>
                <ContentHeader
                    text="Order Status Pending"
                    id="order-pending-heading"
                />
                <div className={classes.inner}>
                    <Typography className={classes.paragraph} variant="body2">
                        Your order status is being confirmed. If you reached
                        this page directly or your payment is still being
                        processed, please check your email for updates or
                        contact support if you have any questions.
                    </Typography>
                    <p>
                        <ButtonLink
                            className={classes.button}
                            variant="outlined"
                            color="primary"
                            to="/"
                        >
                            My Tickets
                        </ButtonLink>
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <ContentHeader text="Order Failed" id="order-failed-heading" />
            <div className={classes.inner}>
                <Typography className={classes.paragraph} variant="body2">
                    There was a problem processing your order. Please try again
                    or contact support if the problem persists.
                    {encryptedTicketId && (
                        <p>
                            <ButtonLink
                                className={classes.button}
                                variant="outlined"
                                color="primary"
                                to={`/try-again/${encryptedTicketId}`}
                            >
                                Try Again
                            </ButtonLink>
                        </p>
                    )}
                </Typography>
            </div>
        </div>
    );
};

export default withStyles(styles)(BankOrderConfirmed);
